import React, { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  HiChevronRight,
  HiBell,
  HiStar,
  HiSparkles,
  HiOutlineMap,
  HiCog6Tooth,
  HiTrophy,
  HiUserCircle,
  HiHeart,
  HiShieldCheck,
  HiUserGroup,
  HiPhoto,
  HiCheck,
  HiPaintBrush,
  HiArrowDownTray,
  HiFlag,
  HiKey,
} from "react-icons/hi2";
import { SiBluetooth } from "react-icons/si";
import { userService } from "../services/userService";
import { trainingTypeService } from "../services/trainingTypeService";
import { organisationService } from "../services/organisationService";
import { ActiveSubscription } from "../services/stripeTypes";
import { usernameService } from "../services/usernameService";
import { UserData } from "../types/user";
import Header from "../components/layout/Header";
import BottomNavBar from "../components/layout/BottomNav";
import Loading from "../components/Loading";
import ComingSoon from "../components/ComingSoon";
import { useSubscription } from "../providers/SubscriptionProvider";
import { useThemeStore } from "../stores/themeStore";
import VerifyEmailSettings from "../components/settings/VerifyEmailSettings";

// Terra Device interface for type safety
interface TerraDevice {
  id: string;
  resource: string;
  connectedAt: string;
  expired?: boolean;
}
interface BackgroundImage {
  id: string;
  url: string;
  filename: string;
  storagePath: string;
}
const BackgroundPickerModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  currentImageUrl?: string;
  userId: string;
  onSuccess: (url: string) => void;
}> = ({ isOpen, onClose, currentImageUrl, userId, onSuccess }) => {
  const { refreshUserData } = useAuth();

  const [images, setImages] = useState<BackgroundImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<string>(currentImageUrl || "");
  const [error, setError] = useState<string | null>(null);

  // Fetch gallery images from Firestore
  useEffect(() => {
    if (!isOpen) return;
    setSelected(currentImageUrl || "");
    setError(null);

    const fetchImages = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, "backgroundGallery"),
          orderBy("uploadedAt", "desc"),
        );
        const snap = await getDocs(q);
        const items: BackgroundImage[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<BackgroundImage, "id">),
        }));
        setImages(items);
      } catch (err) {
        console.error("Error fetching backgrounds:", err);
        setError("Failed to load backgrounds. Please try again.");
      } finally {
        refreshUserData();
        setLoading(false);
      }
    };

    fetchImages();
  }, [isOpen, currentImageUrl]);

  const handleSave = async () => {
    if (selected === currentImageUrl) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await userService.updateUserProfile(userId, {
        backgroundImage: selected,
      });
      onSuccess(selected);
      onClose();
    } catch (err) {
      console.error("Error saving background:", err);
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
      refreshUserData();
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    setError(null);
    try {
      await userService.updateUserProfile(userId, { backgroundImage: "" });
      onSuccess("");
      onClose();
    } catch (err) {
      setError("Failed to remove background.");
    } finally {
      setSaving(false);
      refreshUserData();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-8"
      onClick={onClose}
    >
      <div
        className="bg-base-100 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-8 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-base-content">
              Dashboard Background
            </h3>
            <p className="text-sm text-base-content/50 mt-0.5">
              Choose a background for your dashboard.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-base-200 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="w-4 h-4 text-base-content/60"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 px-8 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <span className="loading loading-spinner loading-md text-base-content/40" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <p className="text-sm text-red-500">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                }}
                className="text-xs text-base-content/60 underline"
              >
                Retry
              </button>
            </div>
          ) : images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <HiPhoto className="w-10 h-10 text-base-content/30" />
              <p className="text-sm text-base-content/50">
                No backgrounds available yet
              </p>
              <p className="text-xs text-base-content/40">Check back soon</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {images.map((image) => {
                const isSelected = selected === image.url;
                return (
                  <button
                    key={image.id}
                    onClick={() => setSelected(image.url)}
                    className={`
                      relative rounded-xl overflow-hidden aspect-video border-2 transition-all duration-150
                      ${
                        isSelected
                          ? "border-base-content shadow-md scale-[1.02]"
                          : "border-transparent hover:border-base-300 hover:shadow-sm"
                      }
                    `}
                  >
                    <img
                      src={image.url}
                      alt={image.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                    {/* Selected checkmark */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-neutral flex items-center justify-center">
                        <HiCheck className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-8 shrink-0 flex flex-col gap-2">
          {error && !loading && (
            <p className="text-xs text-red-500 text-center">{error}</p>
          )}
          <div className="flex justify-between">
            <div>
              {currentImageUrl && (
                <button
                  onClick={handleRemove}
                  disabled={saving}
                  className="flex-1 py-2.5 px-8 rounded-xl text-md text-red-500 hover:bg-red-500/10 border border-red-200 transition-colors font-medium disabled:opacity-50"
                >
                  Restore to Default
                </button>
              )}
            </div>
            <div>
              <button
                onClick={handleSave}
                disabled={saving || loading || images.length === 0}
                className="flex-1 py-2.5 px-8 rounded-xl text-md bg-primary text-white hover:bg-neutral/90 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  "Apply"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
// Username Creation Modal Component
const UsernameCreationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (username: string) => void;
  currentUserId: string;
}> = ({ isOpen, onClose, onSuccess, currentUserId }) => {
  const [newUsername, setNewUsername] = useState("");
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [isCreatingUsername, setIsCreatingUsername] = useState(false);

  // Handle username creation with validation
  const handleCreateUsername = async () => {
    if (!currentUserId || !newUsername.trim()) {
      setUsernameError("Please enter a username");
      return;
    }

    setIsCreatingUsername(true);
    setUsernameError(null);

    try {
      // Validate username availability
      const result = await usernameService.checkUsernameAvailability(
        newUsername.trim(),
      );
      if (!result.available) {
        setUsernameError(result.message);
        setIsCreatingUsername(false);
        return;
      }

      console.log("Username is available, updating user profile...");

      // Update user profile with new username in Firestore
      await userService.updateUserProfile(currentUserId, {
        username: newUsername.trim(),
      });

      console.log(
        "Username successfully saved to database:",
        newUsername.trim(),
      );

      // Notify parent component of success
      onSuccess(newUsername.trim());

      // Reset state
      setNewUsername("");
      setUsernameError(null);
    } catch (error) {
      console.error("Error creating username:", error);
      setUsernameError(
        error instanceof Error
          ? error.message
          : "Failed to create username. Please try again.",
      );
    } finally {
      setIsCreatingUsername(false);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewUsername("");
      setUsernameError(null);
      setIsCreatingUsername(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-base-100 p-8 rounded-lg shadow-xl max-w-md w-full">
        <h3 className="text-xl font-semibold text-base-content mb-4">
          Create Username
        </h3>
        <p className="text-sm text-base-content/70 mb-4">
          Please choose a username for your account. It must be unique and can
          only contain letters, numbers, and underscores.
        </p>
        <div className="mb-4">
          <input
            type="text"
            id="newUsername"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            className="w-full px-3 py-2 border border-base-300 rounded-md bg-base-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your desired username"
            disabled={isCreatingUsername}
          />
          {usernameError && (
            <p className="text-sm text-red-600 mt-2">{usernameError}</p>
          )}
        </div>
        <div className="flex justify-between space-x-2">
          <button
            className=" bg-base-100"
            onClick={onClose}
            disabled={isCreatingUsername}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateUsername}
            disabled={isCreatingUsername || !newUsername.trim()}
          >
            {isCreatingUsername ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : (
              "Create Username"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const Settings: React.FC = () => {
  const { logout, authState, currentUser } = useAuth();
  const { isPremium, isPlusOrHigher, isDefault } = useSubscription();
  const { theme } = useThemeStore();

  const navigate = useNavigate();
  // State for user data from userService
  const [userData, setUserData] = useState<UserData | null>(null);
  const [userDataLoading, setUserDataLoading] = useState(true);
  const [userDataError, setUserDataError] = useState<string | null>(null);

  const [coachingGroupIds, setCoachingGroupIds] = useState<string[]>([]);

  useEffect(() => {
    const fetchOrganisations = async () => {
      if (currentUser?.uid) {
        try {
          const ids = await organisationService.getCoachingGroupIds(
            currentUser.uid,
          );
          setCoachingGroupIds(ids);
        } catch (error) {
          console.error("Error fetching coaching group IDs:", error);
        }
      }
    };

    fetchOrganisations();
  }, [currentUser?.uid]);

  // State for subscription data
  const [subscriptionData, setSubscriptionData] =
    useState<ActiveSubscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  // State for username creation modal
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonFeatureKey, setComingSoonFeatureKey] = useState<
    string | undefined
  >(undefined);

  const [availableSports, setAvailableSports] = useState<
    Record<string, string>
  >({});

  const fetchAvailableSports = async () => {
    try {
      const allSports = await trainingTypeService.fetchSports();
      const sportsMap = allSports.reduce(
        (acc: Record<string, string>, sport) => {
          if (sport.id) acc[sport.id] = sport.name;
          return acc;
        },
        {},
      );
      setAvailableSports(sportsMap);
    } catch (error) {
      console.error("Error fetching available sports:", error);
      setAvailableSports({});
    }
  };

  useEffect(() => {
    // console.log("isPremiunis ", isPremium);
    fetchAvailableSports();
  }, []);
  // Fetch user data and subscription data
  const fetchData = async () => {
    if (!currentUser?.uid) {
      setUserDataLoading(false);
      setSubscriptionLoading(false);
      return;
    }

    try {
      setUserDataLoading(true);
      setSubscriptionLoading(true);
      setUserDataError(null);

      // Fetch both user data and subscription data using service
      const { userData, subscription } = await userService.getUserProfile(
        currentUser.uid,
      );
      setUserData(userData);
      setSubscriptionData(subscription);

      // Check if username is missing and show modal
      if (userData && (!userData.username || userData.username.trim() === "")) {
        setShowUsernameModal(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setUserDataError(
        error instanceof Error ? error.message : "Failed to fetch data",
      );
    } finally {
      setUserDataLoading(false);
      setSubscriptionLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [currentUser?.uid]);
  // Handle successful username creation
  const handleUsernameSuccess = (newUsername: string) => {
    if (userData) {
      setUserData({ ...userData, username: newUsername });
    }
    setShowUsernameModal(false);
  };

  if (authState.loading || userDataLoading) {
    return (
      <div className="min-h-screen bg-base-200 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Loading size="md" text="Loading Settings..." />
        </div>
      </div>
    );
  }

  if (userDataError) {
    return (
      <div className="container max-w-sm mx-auto">
        <div className="card shadow-lg p-8 mt-8 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Error Loading Profile
          </h3>
          <p className="text-base-content/70 mb-6">{userDataError}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="container max-w-sm mx-auto">
        <div className="card shadow-lg p-8 mt-8 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            User data not found
          </h3>
          <p className="text-base-content/70 mb-6">
            It seems your profile information is incomplete. Please complete
            your registration to continue.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/complete-profile")}
          >
            Complete Profile
          </button>
        </div>
      </div>
    );
  }
  const isTrial = (status: string | null | undefined) => {
    if (!status) {
      return "";
    } else if (status === "trialing") {
      return <span className="text-xs text-yellow-600"> ( Trial ) </span>;
    } else return "";
  };
  const getSubscriptionBadge = (
    subscriptionType: string | null | undefined,
  ) => {
    if (!subscriptionType) {
      return <span className="text-xs text-orange-500">No subscription</span>;
    }

    switch (subscriptionType.toLowerCase()) {
      case "platinum":
        return <span className="text-xs text-yellow-600">Platinum</span>;
      case "premium":
        return <span className="text-xs text-green-600">Premium</span>;
      case "coach":
        return <span className="text-xs text-blue-600">Coach</span>;
      case "free":
        return <span className="text-xs text-blue-600">Free</span>;
      case "plus":
        return <span className="text-xs text-green-600">Plus</span>;
      case "default":
        return <span className="text-xs text-base-content/70">Default</span>;
      default:
        return (
          <span className="text-xs text-base-content/70">
            {subscriptionType}
          </span>
        );
    }
  };

  // Helper function to get athlete preferences description
  const getAthletePreferencesDescription = () => {
    const parts: string[] = [];

    // Handle sports
    if (userData?.trainingSportIDs && userData.trainingSportIDs.length > 0) {
      const sportNames = userData.trainingSportIDs
        .map((id: string) => availableSports[id]) // convert ID → name
        .filter(Boolean);

      if (sportNames.length > 0) {
        if (sportNames.length === 1) {
          parts.push(`${sportNames[0]}`);
        } else if (sportNames.length === 2) {
          parts.push(`${sportNames[0]}, ${sportNames[1]}`);
        } else {
          parts.push(`${sportNames[0]} +${sportNames.length - 1} more`);
        }
      }
    }

    // Default
    if (parts.length === 0) {
      return "Set your sports and preferences";
    }

    return parts.join(" • ");
  };

  // Settings menu items - always include Athlete Preferences
  const settingsItems = [
    {
      id: "profile",
      title: "Profile",
      description: "Personal information and privacy settings",
      icon: HiUserCircle,
      onClick: () => navigate("/edit-profile"),
      hasChevron: true,
    },
    {
      id: "subscription",
      title: "Subscription",
      description: subscriptionLoading ? (
        "Loading..."
      ) : (
        <>
          {getSubscriptionBadge(subscriptionData?.subscriptionType)}
          {isTrial(subscriptionData?.stripeDetails?.subscriptionStatus)}
        </>
      ),
      icon: HiCog6Tooth,
      onClick: () => navigate("/settings/subscription"),
      hasChevron: true,
    },
    {
      id: "notifications",
      title: "Push Notifications",
      description: "Manage your notification preferences",
      icon: HiBell,
      onClick: () => navigate("/settings/notifications"),
      hasChevron: true,
    },
    {
      id: "wearable",
      title: "Wearable Devices",
      description:
        userData.deviceIDs && userData.deviceIDs.length > 0
          ? `${
              (userData.deviceIDs as TerraDevice[]).filter(
                (d: TerraDevice) => !d.expired,
              ).length
            } devices connected`
          : "Connect your devices",
      icon: SiBluetooth,
      onClick: () => navigate("/settings/wearable"),
      hasChevron: true,
    },
    {
      id: "eqlx",
      title: "STAXS Points",
      description: "View and manage your points",
      icon: HiTrophy,
      onClick: () => navigate("/settings/eqlx"),
      hasChevron: true,
    },
    {
      id: "athlete-preferences",
      title: "Athlete Preferences",
      description: getAthletePreferencesDescription(),
      icon: HiHeart,
      onClick: () => navigate("/settings/athlete-preferences"),
      hasChevron: true,
    },
    {
      id: "coaches",
      title: "Coaching Groups",
      description:
        coachingGroupIds.length > 0
          ? `${coachingGroupIds.length} ${
              coachingGroupIds.length === 1
                ? "coaching group"
                : "coaching groups"
            }`
          : "View your coaching groups",
      icon: HiUserGroup,
      onClick: () => navigate("/settings/coaches-corner"),
      hasChevron: true,
    },
    {
      id: "coach-code",
      title: "Coach Code",
      description: "Join a coaching group with an invite code",
      icon: HiKey,
      onClick: () => navigate("/settings/coaches-corner#coach-code"),
      hasChevron: true,
    },
    // {
    //   id: 'physical-test',
    //   title: 'Physical Test',
    //   description: 'Set custom daily tests',
    //   icon: HiClipboardDocumentList,
    //   onClick: () => navigate('/settings/physical-test'),
    //   hasChevron: true,
    // },
    {
      id: "goals",
      title: "Goals",
      description: `Your goals and daily targets`,
      icon: HiStar,
      onClick: () => navigate("/settings/goals"),
      hasChevron: true,
    },
    // {
    //   id: 'training-tags',
    //   title: 'Training Tags',
    //   description: 'Customize training categories',
    //   icon: HiTag,
    //   onClick: () => navigate('/settings/training-tags'),
    //   hasChevron: true,
    // },
    {
      id: "ai",
      title: "AI Settings",
      description: "Manage AI-related settings",
      icon: HiSparkles,
      onClick: () => navigate("/settings/ai"),
      hasChevron: true,
      requiredTier: "premium" as const,
      featureKey: "wizardAi",
    },
    {
      id: "security",
      title: "Security",
      description: "Manage password and account settings",
      icon: HiShieldCheck,
      onClick: () => navigate("/settings/security"),
      hasChevron: true,
    },
    {
      id: "my-reports",
      title: "My Reports",
      description: "View reports you've submitted",
      icon: HiFlag,
      onClick: () => navigate("/settings/my-reports"),
      hasChevron: true,
    },

    {
      id: "start-walkthrough",
      title: "Start Walkthrough",
      description: "Replay the app walkthrough and guided tips",
      icon: HiOutlineMap,
      hasChevron: true,
      onClick: () => navigate("/dashboard?walkthrough=true"),
    },
    {
      id: "background",
      title: "Dashboard Background",
      description: userData?.backgroundImage
        ? "Custom background set"
        : "Choose a background image",
      icon: HiPhoto,
      onClick: () => setShowBackgroundModal(true),
      hasChevron: true,
      requiredTier: "plus" as const,
      featureKey: "dashboardBackground",
    },
    {
      id: "appearance",
      title: "Appearance",
      description:
        theme === "system" ? "System" : theme === "dark" ? "Dark" : "Light",
      icon: HiPaintBrush,
      onClick: () => navigate("/settings/appearance"),
      hasChevron: true,
    },
    {
      id: "export-data",
      title: "Export Data",
      description: "Download your training data as CSV",
      icon: HiArrowDownTray,
      onClick: () => navigate("/downloaddata"),
      hasChevron: true,
      requiredTier: "plus" as const,
      featureKey: "exportCsv",
    },
  ];
  const handleBackgroundSuccess = (url: string) => {
    if (userData) setUserData({ ...userData, backgroundImage: url });
  };
  return (
    <div className="w-full pt-16">
      <Header />

      <div className="container max-w-3xl mx-auto py-8 px-8 pb-32">
        <h1 className="text-3xl font-semibold text-base-content mb-8">
          Settings
        </h1>

        {/* User Profile Header */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-base-300">
              {userData.imageUrlNormal || userData.imageUrl ? (
                <img
                  src={userData.imageUrlNormal || userData.imageUrl}
                  alt={`${userData.name}'s profile`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <HiUserCircle className="w-12 h-12 text-base-content/50" />
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-base-content">
                {userData.name}
              </h2>
              <p className="text-base-content/70">{userData.email}</p>
              {userData.username && (
                <p className="text-sm text-base-content/60">
                  @{userData.username}
                </p>
              )}
            </div>
          </div>
        </div>

        <VerifyEmailSettings
          email={userData.email || currentUser?.email || ""}
          verified={Boolean(userData.isEmailVerified) || Boolean(currentUser?.emailVerified)}
          onVerified={() => setUserData({ ...userData, isEmailVerified: true })}
        />

        {/* Settings Menu */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 divide-y divide-base-300">
          {settingsItems
            .filter((item) => {
              // Hide walkthrough option for casual users
              if (item.id === "start-walkthrough" && userData?.casualMode) {
                return false;
              }
              // Hide items where show is explicitly false
              if ("show" in item && item.show === false) {
                return false;
              }
              return true;
            })
            .map((item) => {
              const IconComponent = item.icon;
              const isGated =
                item.requiredTier &&
                isDefault &&
                (item.requiredTier === "plus" ? !isPlusOrHigher : !isPremium);

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (isGated) {
                      setComingSoonFeatureKey(item.featureKey);
                      setShowComingSoonModal(true);
                    } else {
                      item.onClick();
                    }
                  }}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-base-200 transition-colors duration-200 text-left focus:outline-none focus:bg-base-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-base-content/50" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-md font-bold text-base-content font-heading">
                        {item.title}
                      </p>

                      <p
                        className="text-sm text-base-content/60
                        break-words whitespace-normal
                        sm:truncate sm:whitespace-nowrap"
                      >
                        {" "}
                        {item.description}{" "}
                      </p>
                    </div>
                  </div>
                  {item.hasChevron && (
                    <HiChevronRight className="w-5 h-5 text-base-content/50" />
                  )}
                </button>
              );
            })}
        </div>

        {/* Logout Button */}
        <div className="mt-8">
          <button
            onClick={logout}
            disabled={authState.loading}
            className="w-full bg-red-500/10 hover:bg-red-500/15 text-red-500 font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <span className="font-heading">Sign Out</span>
          </button>
        </div>

        {/* Username Creation Modal */}
        {showUsernameModal && (
          <UsernameCreationModal
            isOpen={showUsernameModal}
            onClose={() => setShowUsernameModal(false)}
            onSuccess={handleUsernameSuccess}
            currentUserId={currentUser?.uid || ""}
          />
        )}
      </div>
      <BackgroundPickerModal
        isOpen={showBackgroundModal}
        onClose={() => setShowBackgroundModal(false)}
        currentImageUrl={userData?.backgroundImage}
        userId={currentUser?.uid || ""}
        onSuccess={handleBackgroundSuccess}
      />

      {/* Coming Soon Modal for gated features */}
      {showComingSoonModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowComingSoonModal(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <ComingSoon featureKey={comingSoonFeatureKey} showButton={true} />
          </div>
        </div>
      )}
      {/* Bottom Navigation */}
      <BottomNavBar />
    </div>
  );
};

export default Settings;
